import type { Variant } from '../gameTypes.js';
import { JUMP_VARIANT_TUNING, type JumpChart } from '../jumpChart.js';
import { packJumpGrid, JUMP_TILE, type JumpCharGrid, type JumpGrid } from '../jumpGrid.js';
import { firstUnclearableJumpHazardId } from '../engine/jump/headlessRunner.js';

/**
 * The reachability filter that makes a generated Jump grid SHIPPABLE: drop the
 * spikes / saws the cube can't actually clear, using the SAME greedy planner the
 * perfect-replay builder and the all-maps test use ({@link firstUnclearableJumpHazardId})
 * — so "the generator ships it ⇔ the planner clears it" holds by construction.
 *
 * Spike-walls and saws are the only onset-driven hazards that can occasionally be
 * unthreadable; pattern terrain (platforms / staircases / pits / corridors) is
 * clearable by construction and is never touched here. Iterates: ask the planner
 * for the first unclearable hazard, clear that column's hazard cells, retry.
 * Monotonic and bounded (the drop budget is the hazard-cell count).
 */
export function filterUnclearableJumpGrid(
  grid: JumpGrid,
  ctx: { slug: string; variant: Variant },
): JumpGrid {
  let current = grid;
  const budget = countHazardCells(grid) + 1;
  for (let i = 0; i < budget; i += 1) {
    const failed = firstUnclearableJumpHazardId(makeProbeChart(current, ctx));
    if (failed === null) {
      return current;
    }
    const match = /^([so])(\d+)_/.exec(failed);
    if (!match) {
      // A terrain leading-face / ceiling reported unclearable — should not
      // happen given clearable-by-construction terrain. Stop rather than spin.
      console.warn(
        `[jumpGridFilter] planner flagged non-hazard-cell ${failed} for "${ctx.slug}:${ctx.variant}"; stopping filter`,
      );
      return current;
    }
    const tile = match[1] === 's' ? JUMP_TILE.spikeUp : JUMP_TILE.saw;
    const col = Number(match[2]);
    current = clearColumnTile(current, col, tile);
  }
  return current;
}

/** Count spike + saw cells (the filter's drop budget). */
function countHazardCells(grid: JumpGrid): number {
  let n = 0;
  for (const line of grid.tiles) {
    for (const ch of line) {
      if (ch === JUMP_TILE.spikeUp || ch === JUMP_TILE.saw) {
        n += 1;
      }
    }
  }
  return n;
}

/** Return a new grid with every `tile` cell in `col` cleared to air. */
function clearColumnTile(grid: JumpGrid, col: number, tile: string): JumpGrid {
  const charGrid: JumpCharGrid = grid.tiles.map((line) => line.split(''));
  for (let row = 0; row < grid.rows; row += 1) {
    if (charGrid[row][col] === tile) {
      charGrid[row][col] = JUMP_TILE.air;
    }
  }
  return packJumpGrid(charGrid, grid.cellBeats, grid.cellPx, grid.hints);
}

/** A throwaway chart wrapping `grid` for the planner during filtering. */
function makeProbeChart(grid: JumpGrid, ctx: { slug: string; variant: Variant }): JumpChart {
  const tuning = JUMP_VARIANT_TUNING[ctx.variant];
  const totalBeats = grid.cols * grid.cellBeats;
  return {
    mode: 'jump',
    id: ctx.slug,
    slug: ctx.slug,
    title: ctx.slug,
    description: '',
    bpm: 120,
    totalBeats,
    difficulty: 1,
    variant: ctx.variant,
    palette: {
      bg: '#000000',
      bgAccent: '#111111',
      player: '#ffffff',
      hazard: '#ff0000',
      pad: '#00ff00',
      padPerfect: '#00ffff',
      goal: '#00ff00',
      accent: '#ffff00',
      ui: '#ffffff',
    },
    audio: { src: 'x', bpm: 120, offsetMs: 0, durationSec: 1, attribution: '', onsets: [] },
    gravity: tuning.gravity,
    jumpVelocity: tuning.jumpVelocity,
    shortJumpVelocity: tuning.shortJumpVelocity,
    playerSize: tuning.playerSize,
    grid,
  };
}
